import React from "react";

const COMPANY = "Santa Rosa Commercial Investment, Inc. (SRCI Pharm-Route)";
const SUPPORT_EMAIL = "santarosacommercial@gmail.com";

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-lg font-semibold text-slate-900 mb-2">{title}</h2>
      <div className="text-sm text-slate-700 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <div data-testid="page-privacy-policy" className="min-h-screen bg-slate-50 px-6 py-12">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12">
        <a href="/" className="text-xs text-primary font-medium mb-6 inline-block">&larr; Back to sign-in</a>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
        <p className="text-xs text-slate-500 mb-8">Last updated: June 2026</p>

        <Section title="1. Overview">
          <p>
            {COMPANY} respects your privacy and is committed to protecting personal and protected
            health information (PHI) entrusted to us by the pharmacies we serve and their patients.
            This Privacy Policy describes what data we collect, how we use it, and your choices.
          </p>
        </Section>

        <Section title="2. Information we collect">
          <p>To deliver prescription orders we collect:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li><strong>Patient details</strong> — name, address, phone, prescription items (PHI under HIPAA)</li>
            <li><strong>Pharmacy details</strong> — name, address, contact info</li>
            <li><strong>Driver / staff details</strong> — name, email, role, vehicle plate</li>
            <li><strong>Delivery data</strong> — GPS coordinates of stops, signature image, POD photos, timestamps</li>
            <li><strong>Payment info</strong> — Square transaction references (we never store full card numbers)</li>
          </ul>
        </Section>

        <Section title="3. HIPAA compliance">
          <p>
            We act as a <strong>Business Associate</strong> to the participating pharmacies. PHI is
            handled in accordance with the Business Associate Agreement (BAA) executed with each
            pharmacy. Access is restricted to authorized personnel on a need-to-know basis and
            logged in our audit system.
          </p>
        </Section>

        <Section title="4. How we use information">
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>To plan, dispatch, and complete prescription deliveries</li>
            <li>To notify patients and pharmacies of delivery status via SMS/email</li>
            <li>To generate proof-of-delivery (POD) records the pharmacy may need for audits</li>
            <li>To bill participating pharmacies for completed deliveries</li>
          </ul>
          <p>
            We do <strong>not</strong> sell, rent, or trade your information. We do not use PHI
            for marketing.
          </p>
        </Section>

        <Section title="5. Third-party services">
          <p>We use the following processors strictly for the purposes above:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li><strong>Google Maps</strong> — address geocoding and route planning</li>
            <li><strong>OSRM</strong> — driving-distance computation</li>
            <li><strong>Twilio</strong> — SMS delivery notifications (when configured)</li>
            <li><strong>Gmail / Google Workspace</strong> — invoice and notification emails</li>
            <li><strong>Square</strong> — credit-card payment processing for invoices</li>
            <li><strong>MongoDB Atlas</strong> — encrypted database hosting</li>
          </ul>
          <p>Each provider is contractually bound to safeguard data they process on our behalf.</p>
        </Section>

        <Section title="6. Data retention">
          <p>
            We retain PHI and delivery records for the longer of (a) the period the pharmacy
            requires for its own regulatory record-keeping (typically 7 years), or (b) the period
            required by federal/state law. After this period the data is securely deleted.
          </p>
        </Section>

        <Section title="7. Your choices">
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>You may text <strong>STOP</strong> at any time to opt out of SMS notifications.</li>
            <li>You may request a copy or correction of your PHI by contacting your pharmacy.</li>
            <li>You may request deletion of your account-level data by emailing us directly.</li>
          </ul>
        </Section>

        <Section title="8. Security">
          <p>
            All data is transmitted over HTTPS (TLS 1.2+), encrypted at rest, and access-controlled
            via JWT-based authentication with role-based permissions (Super Admin, Dispatcher,
            Pharmacy Staff, Driver).
          </p>
        </Section>

        <Section title="9. Children">
          <p>Our service is not directed to children under 13. We do not knowingly collect data from anyone under 13.</p>
        </Section>

        <Section title="10. Changes to this policy">
          <p>
            We may update this Privacy Policy from time to time. The "Last updated" date at the
            top of this page reflects the latest revision. Material changes will be communicated
            to participating pharmacies.
          </p>
        </Section>

        <Section title="11. Contact">
          <p>
            Privacy questions or HIPAA-related requests? Email{" "}
            <a className="text-primary underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
        </Section>

        <p className="text-xs text-slate-400 mt-8 italic">
          This is a starter template. Have your legal counsel review and customize before going live.
        </p>
      </div>
    </div>
  );
}
